import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { IssueProduct } from '@gooduncles/gu-app-schema';
import { Button, Checkbox, Input, Modal, Segmented, notification } from 'antd';
import { ButtonColorType } from 'antd/es/button';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { getProduct } from 'src/utils/firebase/firebase-short-cut';
import { parseOrderProductsToIssueProduct } from 'src/utils/issue/issue-util';

import classes from './IssueProductsEditModal.module.scss';

import AlgoliaSearch from '../AlgoriaSearch/AlgoriaSearch';

const MODAL_CONFIG = {
  WIDTH: 800,
  TABS: {
    ORDER_PRODUCTS: 'order-products',
    ALL_PRODUCTS: 'all-products',
  },
} as const;

type TabType = 'order-products' | 'all-products';

interface IssueProductsEditModalProps {
  color: ButtonColorType;
  buttonText: string;
  title: string;
  currentProducts: IssueProduct[];
  selectableProducts: IssueProduct[];
  editable: boolean;
  onUpdateProducts: (products: IssueProduct[]) => Promise<void>;
}

const IssueProductsEditModal: FC<IssueProductsEditModalProps> = ({
  color,
  buttonText,
  title,
  currentProducts,
  selectableProducts,
  editable,
  onUpdateProducts,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>(MODAL_CONFIG.TABS.ORDER_PRODUCTS);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setActiveTab(MODAL_CONFIG.TABS.ORDER_PRODUCTS);
    }
  }, [open]);

  const updateProducts = useCallback(
    async (updateFn: (products: IssueProduct[]) => IssueProduct[]) => {
      try {
        await onUpdateProducts(updateFn(currentProducts));
      } catch (error) {
        console.error('상품 업데이트 실패:', error);
        notification.error({
          message: '상품 업데이트 실패',
          description: errorObjectToString(error),
        });
      }
    },
    [currentProducts, onUpdateProducts]
  );

  const handleProductToggle = useCallback(
    (product: IssueProduct, checked: boolean) =>
      updateProducts((products) =>
        checked ? [...products, product] : products.filter((p) => p.productId !== product.productId)
      ),
    [updateProducts]
  );

  const isProductSelected = (product: IssueProduct) => {
    return currentProducts.some((p) => p.productId === product.productId);
  };

  const filteredSelectableProducts = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return selectableProducts.filter((product) => product.productName.toLowerCase().includes(searchLower));
  }, [selectableProducts, searchTerm]);

  const handleRemoveProduct = useCallback(
    (product: IssueProduct) => updateProducts((products) => products.filter((p) => p.productId !== product.productId)),
    [updateProducts]
  );

  const handleAddFromSearch = async (productId: string) => {
    try {
      const product = productId ? await getProduct(productId) : null;
      if (!product) {
        throw new Error('상품을 찾을 수 없습니다.');
      }
      const newProduct = parseOrderProductsToIssueProduct({
        ...product,
        volume: 1,
      });

      if (!currentProducts.some((p) => p.productId === productId)) {
        await updateProducts((products) => [...products, newProduct]);
      }
    } catch (error) {
      console.error('검색 상품 추가 실패:', error);
      notification.error({ message: '상품을 추가할 수 없습니다.', description: errorObjectToString(error) });
    }
  };

  return (
    <>
      {editable && (
        <Button
          color={color}
          variant='text'
          icon={<EditOutlined />}
          iconPosition='end'
          onClick={() => setOpen(!open)}
          block>
          {buttonText}
        </Button>
      )}
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        title={title}
        width={MODAL_CONFIG.WIDTH}
        footer={[
          <Button key='close' onClick={() => setOpen(false)}>
            닫기
          </Button>,
        ]}>
        <div className={classes.modalContainer}>
          <div className={classes.leftPanel}>
            <Segmented
              value={activeTab}
              onChange={setActiveTab}
              options={[
                { label: '연관 상품 목록', value: MODAL_CONFIG.TABS.ORDER_PRODUCTS },
                { label: '전체 상품 검색', value: MODAL_CONFIG.TABS.ALL_PRODUCTS },
              ]}
              className={classes.segmentedContainer}
            />

            {activeTab === MODAL_CONFIG.TABS.ORDER_PRODUCTS && (
              <div className={classes.tabContent}>
                <Input
                  placeholder='상품명으로 검색'
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={classes.searchInput}
                />
                <div className={classes.productList}>
                  {filteredSelectableProducts.map((product) => (
                    <div key={product.productId} className={classes.productItem}>
                      <Checkbox
                        checked={isProductSelected(product)}
                        onChange={(e) => handleProductToggle(product, e.target.checked)}>
                        {product.productName}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === MODAL_CONFIG.TABS.ALL_PRODUCTS && (
              <div className={classes.tabContent}>
                <AlgoliaSearch onSearch={handleAddFromSearch} placeholder='전체 상품에서 검색하여 추가' size='middle' />
              </div>
            )}
          </div>
          <div className={classes.rightPanel}>
            <h3 className={classes.titleContainer}>등록 상품 - {currentProducts.length}건</h3>
            <div className={classes.selectedProductsList}>
              {currentProducts.map((product) => (
                <div key={product.productId} className={classes.selectedProductItem}>
                  <span>{product.productName}</span>
                  <Button
                    type='text'
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveProduct(product)}
                    className={classes.deleteButton}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IssueProductsEditModal;
export type { IssueProductsEditModalProps };
